import { axiosInstance, httpAuth } from '../config/apiClient'
import endpoints from '../constants/endpoints'

const DEVICE_ID_KEY = 'web_push_device_id'
const isDev = process.env.NODE_ENV === 'development'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY)
  if (existing) return existing

  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  const hex = Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
  const deviceId = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  localStorage.setItem(DEVICE_ID_KEY, deviceId)
  return deviceId
}

export function waitForActivation(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  return new Promise((resolve, reject) => {
    const sw = registration.installing || registration.waiting || registration.active
    if (!sw) {
      reject(new Error('No service worker found on registration'))
      return
    }
    if (sw.state === 'activated') {
      resolve(registration)
      return
    }
    sw.addEventListener('statechange', function listener() {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', listener)
        resolve(registration)
      } else if (sw.state === 'redundant') {
        sw.removeEventListener('statechange', listener)
        reject(new Error('Service worker became redundant'))
      }
    })
  })
}

export async function subscribeToPush(vapidPublicKey: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[WebPush] Push notifications not supported in this browser')
    return
  }

  if ('Notification' in window && Notification.permission === 'denied') {
    console.warn('[WebPush] skipped: notification permission is denied')
    return
  }

  if ('Notification' in window && Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      console.warn('[WebPush] skipped: notification permission was not granted', permission)
      return
    }
  }

  const reg =
    (await navigator.serviceWorker.getRegistration('/push-sw.js')) ??
    (await navigator.serviceWorker.register('/push-sw.js'))
  const registration = await waitForActivation(reg)

  const existing = await registration.pushManager.getSubscription()
  if (isDev) {
    console.info(existing ? '[WebPush] existing browser subscription found' : '[WebPush] creating browser subscription')
  }

  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }))

  const p256dhBuffer = subscription.getKey('p256dh')
  const authBuffer = subscription.getKey('auth')
  if (!p256dhBuffer || !authBuffer) {
    throw new Error('[WebPush] Missing subscription keys')
  }
  const p256dh = arrayBufferToBase64(p256dhBuffer)
  const auth = arrayBufferToBase64(authBuffer)
  const deviceId = getOrCreateDeviceId()

  if (isDev) {
    console.info('[WebPush] syncing browser subscription with backend', { deviceId, endpoint: subscription.endpoint })
  }
  await httpAuth.put(endpoints.updateUser, {
    push_tokens: {
      token: subscription.endpoint,
      device_id: deviceId,
      platform: 'web',
      web_keys: { p256dh, auth },
      created_at_ts: Math.floor(Date.now() / 1000),
    },
  })
  if (isDev) console.info('[WebPush] backend subscription sync complete', { deviceId })
}

export async function unsubscribeFromPush(authToken?: string | null): Promise<void> {
  if (!('serviceWorker' in navigator)) return

  const registration = await navigator.serviceWorker.getRegistration('/push-sw.js')
  if (registration) {
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
    }
  }

  const deviceId = localStorage.getItem(DEVICE_ID_KEY)
  if (deviceId) {
    const payload = { device_id: deviceId, platform: 'web' }
    if (authToken) {
      await axiosInstance.put(endpoints.logout, payload, { headers: { Authorization: authToken } })
      return
    }
    await httpAuth.put(endpoints.logout, payload)
  }
}
